import React, { useEffect, useState } from "react";
import "./user.css";
import {
  ChevronLeft,
  ChevronRight,
  EllipsisVertical,
  PlusIcon,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import ConfirmModal from "./ConfirmModal";
import apiServiceHandler from "../../service/apiService";
import "toastr/build/toastr.min.css";
import toastr from "toastr";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import InviteModal from "./InviteModal";
import { getColorFromName } from "../../utils/getColor";
import HashLoader from "react-spinners/HashLoader";
import ReactPaginate from "react-paginate";
import Select from "react-select";

const headers = ["NAME", "ROLE", "STATUS", "STG90", "Lab", "PBA", "CCA"];

const options = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
  { value: "invited", label: "Invited" },
];

const customStyles = {
  control: (provided, state) => ({
    ...provided,
    boxShadow: 'none',
    border: state.isFocused ? '1px solid #ccc' : '1px solid #ddd',
    outline: 'none',
    '&:hover': {
      border: '1px solid #aaa',
    },
  })
};

const User = () => {
  const [acceptModal, setAcceptModal] = useState(false);
  const [inviteModal, setInviteModal] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState("");
  const [selectedUser, setSelectedUser] = useState("");
  const [allUsers, setAllUsers] = useState([]);
  const [allUsersLength, setAllUsersLength] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedOption, setSelectedOption] = useState({ value: "active", label: "Active" });
  const [refreshSelect, setRefreshSelect] = useState(false);

  const toggleAcceptModal = (user, course) => {
    setSelectedUser(user);
    setSelectedCourse(course);
    setAcceptModal(!acceptModal);
  };

  const toggleInviteModal = () => {
    setInviteModal(!inviteModal);
  };

  const toggleCourseAcceptance = async (user, course) => {
    await updateUser(user, course);
    toggleAcceptModal();
  };

  const toggleSelectFilter = (selected) => {
    setRefreshSelect(true);
    setSelectedOption(selected);
  }

  const getAllUsers = async () => {
    try {
      setLoading(true);
      const response = await apiServiceHandler(
        "GET",
        `api/user/all/users?page=${usersOffset}&status=${selectedOption.value}`,
        {}
      );

      if (response.success) {
        setAllUsers(response?.allUsers);
        setAllUsersLength(response?.totalUsers);
        setLoading(false);
      }
    } catch (error) {
      console.error("Error getting getAllUsers:", error);
      setLoading(false);
    }
  };

  const handleRoleChange = (value, user) => {
    updateUser(user, value);
  };

  const handleStatusChange = (e, user) => {
    updateUser(user, "status");
  };

  const updateUser = async (user, value) => {
    try {
      let payload = {};
      if (value == "Superadmin") {
        payload = {
          _id: user._id,
          superadmin: 1,
          admin: 1,
        };
      } else if (value == "Admin") {
        payload = {
          _id: user._id,
          admin: 1,
          superadmin: 2
        };
      } else if (value == "Student") {
        payload = {
          _id: user._id,
          admin: 2,
          superadmin: 2
        };
      } else if (value == "status") {
        payload = {
          _id: user._id,
          status: user.status === 1 ? 0 : 1,
        };
      } else if (
        value == "stg90" ||
        value == "boa" ||
        value == "pba" ||
        value == "club84"
      ) {
        payload = {
          _id: user._id,
          [value]: user.courses_access[value] === 1 ? 2 : 1,
        };
      }
      const response = await apiServiceHandler("PATCH", "api/user/update", {
        payload,
      });
      // console.log(response, "Data is here...");
      if (response.auth) {
        getAllUsers();
        toastr.success("Access changed successfully");
      }
    } catch (error) {
      console.error("Error updating role:", error);
    }
  };

  const filteredUsers = allUsers.filter((user) => {
    return (
      user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email.toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  const usersPerPage = 10;
  const [usersOffset, setUsersOffset] = useState(0);
  const pageCountUsers = Math.ceil(allUsersLength / usersPerPage);

  const handlePageClick = (event) => {
    setUsersOffset(event.selected);
  };

  useEffect(() => {
    setUsersOffset(0);
    getAllUsers();
    setRefreshSelect(false);
  }, [refreshSelect]);

  useEffect(() => {
    getAllUsers();
  }, [usersOffset]);

  const handleResent = async (user) => {
    try {
      const response = await apiServiceHandler("POST", "api/auth/invite/user/resent", { userId: user._id });
      // console.log(response, 'resent invitation response is here');
      if (response.auth) {
        toastr.success(response.message);
      } else {
        toastr.error(response.message || "Failed to invite user. Please try again.");
      }
    } catch (error) {
      toastr.error(error.response.data.message);
      console.error(error.response);
    }
  }

  const navigate = useNavigate()

  const startNewChat = async (id) => {
    const payload = {
      partnerUserId: [id],
    };

    try {
      const response = await apiServiceHandler(
        "POST",
        "api/chat/add/conversation",
        payload
      );
      if (response.status) {
        navigate('/chat')
      }
    } catch (error) {
      console.error("Failed to start a conversation", error);
    }
  };

  return (
    <div className="">
      <div className="user-page-content">
        <div className="users-page-header">
          <div className="users-page-header-top">
            <div className="users-page-header-content">
              <div className="users-page-heading">Users</div>
            </div>
            <div className="users-page-header-actions">
              <div className="search-field">
                <div class="text-input-wrapper">
                  <input
                    type="text"
                    className="text-input"
                    placeholder="search by name, email"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  <span className="icon">
                    <svg
                      viewBox="0 0 19 19"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="icon__svg"
                      preserveAspectRatio="xMidYMid meet"
                      style={{ width: "20px", height: "20px" }}
                    >
                      <path
                        d="M7.25 0C3.258 0 0 3.258 0 7.25s3.258 7.25 7.25 7.25a7.198 7.198 0 004.36-1.475l4.683 4.682a1 1 0 101.414-1.414l-4.682-4.683A7.198 7.198 0 0014.5 7.25C14.5 3.258 11.242 0 7.25 0zm0 2a5.235 5.235 0 015.25 5.25c0 1.398-.543 2.66-1.426 3.599a1 1 0 00-.223.223A5.229 5.229 0 017.25 12.5 5.235 5.235 0 012 7.25 5.235 5.235 0 017.25 2z"
                        fill="#d1b794"
                      ></path>
                    </svg>
                  </span>
                </div>
              </div>
              <div style={{ margin: '10px', width: '120px' }}>
                <div class="select__label">Filter</div>
                <Select
                  defaultValue={selectedOption}
                  onChange={toggleSelectFilter}
                  options={options}
                  placeholder=""
                  styles={customStyles}
                />
              </div>
              <button className="invite-btn" onClick={toggleInviteModal}>
                {/*
                <span class="icon-plus">
                  <PlusIcon />
                </span>
                */}
                <span class="invite-btn-label">Invite</span>
              </button>
            </div>
          </div>
        </div>

        <div className="user-page">
          <div className="members">
            <div className="users-data-table">
              <div className="users-data-table-table">
                <div className="users-data-table-header">
                  {headers.map((header, index) => (
                    <div className="users-data-table-header-cell" key={index}>
                      <div style={{ display: "flex" }}>
                        <span class="text-label" style={{ display: "block" }}>
                          {header}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                {!loading ? (
                  filteredUsers && filteredUsers?.length > 0 ? (
                    filteredUsers.map((user, index) => (
                      <div className="users-data-table-body" key={index}>
                        <div
                          className={`users-data-table-row-${index % 2 == 0 ? "odd" : "even"
                            }`}
                        >
                          <div
                            className="user-data-table-cell"
                          // style="vertical-align: middle;"
                          >
                            <div className="user-data-table-cell-body">
                              <div className="name-column">
                                <div
                                  className="user-avatar"
                                  style={{
                                    backgroundColor: getColorFromName(
                                      user.name
                                    ),
                                  }}
                                >
                                  {user && user.image ? (
                                    <img
                                      className="avatar__image"
                                      src={user.image}
                                      alt="User Avatar"
                                    />
                                  ) : (
                                    <div className="user__avatar">
                                      {user?.name.substr(0, 1)}
                                    </div>
                                  )}
                                </div>
                                <div className="name-column-info">
                                  <div className="name-description">
                                    {user.name}
                                  </div>
                                  <div className="email-description">
                                    {user.email}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div
                            className="user-data-table-cell"
                          //   style="vertical-align: middle;"
                          >
                            <div className="user-data-table-cell-body">
                              <div className="name-column">
                                <div className="name-column-info">
                                  <select
                                    className="dropdown-toggle"
                                    value={user.superadmin ? "Superadmin" : (user.admin ? "Admin" : "Student")}
                                    onChange={(e) => handleRoleChange(e.target.value, user)}
                                  >
                                    <option value="Superadmin">Superadmin</option>
                                    <option value="Admin">Admin</option>
                                    <option value="Student">Student</option>
                                  </select>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div
                            className="user-data-table-cell"
                          //   style="vertical-align: middle;"
                          >
                            <div className="user-data-table-cell-body">
                              <div className="name-column">
                                <div className="name-column-info">
                                  {user.invited && user.password != true && user.status == 2 ? <select
                                    className="dropdown-toggle"
                                    value='Invited'
                                    onChange={(e) =>
                                      handleStatusChange(e, user)
                                    }
                                  >
                                    <option value="Invited">Invited</option>
                                  </select> : <select
                                    className="dropdown-toggle"
                                    value={
                                      user.status === 1 ? "Active" : "Inactive"
                                    }
                                    onChange={(e) =>
                                      handleStatusChange(e, user)
                                    }
                                  >
                                    <option value="Active">Active</option>
                                    <option value="Inactive">Inactive</option>
                                  </select>}
                                </div>
                              </div>
                            </div>
                          </div>
                          {/* stg90 Course */}
                          <div className="user-data-table-cell">
                            <div className="user-data-table-cell-body">
                              <div className="name-column">
                                <div className="name-column-info">
                                  <button
                                    className="name-description"
                                    onClick={() =>
                                      toggleAcceptModal(user, "stg90")
                                    }
                                  >
                                    {user.courses_access.stg90 === 1 ? (
                                      <span>&#10003;</span>
                                    ) : (
                                      "X"
                                    )}
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Lab Course */}
                          <div className="user-data-table-cell">
                            <div className="user-data-table-cell-body">
                              <div className="name-column">
                                <div className="name-column-info">
                                  <button
                                    className="name-description"
                                    onClick={() =>
                                      toggleAcceptModal(user, "boa")
                                    }
                                  >
                                    {user.courses_access.boa === 1 ? (
                                      <span>&#10003;</span>
                                    ) : (
                                      "X"
                                    )}
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* pba Course */}
                          <div className="user-data-table-cell">
                            <div className="user-data-table-cell-body">
                              <div className="name-column">
                                <div className="name-column-info">
                                  <button
                                    className="name-description"
                                    onClick={() =>
                                      toggleAcceptModal(user, "pba")
                                    }
                                  >
                                    {user.courses_access.pba === 1 ? (
                                      <span>&#10003;</span>
                                    ) : (
                                      "X"
                                    )}
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* CCA Course */}
                          <div className="user-data-table-cell">
                            <div className="user-data-table-cell-body">
                              <div className="name-column">
                                <div className="name-column-info">
                                  <button
                                    className="name-description"
                                    onClick={() =>
                                      toggleAcceptModal(user, "club84")
                                    }
                                  >
                                    {user.courses_access.club84 === 1 ? (
                                      <span>&#10003;</span>
                                    ) : (
                                      "X"
                                    )}
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                          {(selectedOption.value === 'invited') &&
                            <div className="user-data-table-cell">
                              <div className="resent-btn" onClick={() => handleResent(user)}>Resend</div>
                            </div>
                          }
                          <div className="user-data-table-cell chat-action" onClick={() => startNewChat(user?._id)}>
                            <Link className="icon-chat">
                              <svg
                                viewBox="0 0 20 20"
                                fill="#d1b794"
                                xmlns="http://www.w3.org/2000/svg"
                                class="icon__svg"
                                preserveAspectRatio="xMidYMid meet"
                                style={{ width: "20px", height: "20px" }}
                              >
                                <path d="M3.25 0A3.254 3.254 0 000 3.25v11.018a1.246 1.246 0 001.251 1.25c.259 0 .516-.08.734-.239L6.493 12h6.257A3.254 3.254 0 0016 8.75v-5.5A3.254 3.254 0 0012.75 0h-9.5zM17 4.01v4.74A4.253 4.253 0 0112.75 13H6.82l-2.225 1.615A3.245 3.245 0 007.25 16h6.255l4.51 3.28a1.242 1.242 0 001.3.1c.425-.21.685-.64.685-1.11V7.25a3.258 3.258 0 00-3-3.24z"></path>
                              </svg>
                            </Link>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div
                      style={{
                        position: "relative",
                        left: "260%",
                        marginTop: "1rem",
                      }}
                    >
                      No Users Found.
                    </div>
                  )
                ) : (
                  <div className="users-loader">
                    <HashLoader color="#d1b794" size={20} />
                    <p>Loading users</p>
                  </div>
                )}
              </div>
              {filteredUsers && filteredUsers?.length > 0 ? <div
                style={loading ? { display: 'none' } : {
                  display: "flex",
                  justifyContent: "center",
                  padding: 20,
                  width: "100%",
                  alignItems: "center",
                }}
              >
                <ReactPaginate
                  containerClassName={"pagination"}
                  pageClassName={"user-page-item"}
                  activeClassName={"user-active"}
                  onPageChange={handlePageClick}
                  pageCount={pageCountUsers}
                  forcePage={usersOffset}
                  nextLabel={
                    pageCountUsers > 1 ? <ChevronRight className="pagination-btn" size={40}
                      style={{
                        fontSize: 16,
                        height: 25,
                        color: "#604b51",
                        background: "#d1b794",
                        borderRadius: "50px",
                        width: "fit-content",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    /> : null
                  }
                  previousLabel={
                    pageCountUsers > 1 ? <ChevronLeft className="pagination-btn" size={40}
                      style={{
                        fontSize: 16,
                        width: 150,
                        height: 25,
                        color: "#604b51",
                        background: "#d1b794",
                        borderRadius: "50px",
                        width: "fit-content",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    /> : null
                  }
                />
              </div> : ''}
            </div>
          </div>
        </div>
      </div>

      <ConfirmModal
        isOpen={acceptModal}
        onClose={toggleAcceptModal}
        toggleCourseAcceptance={toggleCourseAcceptance}
        selectedUser={selectedUser}
        selectedCourse={selectedCourse}
      />
      <InviteModal isOpen={inviteModal} onClose={toggleInviteModal} />
    </div>
  );
};

export default User;
